import { Node } from '@alfresco/js-api';
import {
  Component,
  EventEmitter,
  OnDestroy,
  Output,
  ViewEncapsulation,
} from '@angular/core';
import { DatePipe } from '@angular/common';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';

import { DocumentVersion } from '../../model/document-version.model';
import { WkCompareService } from '../../services/wk-compare.service';
import { getAppSelection } from '../../store/selectors/app.custom-selectors';
import { AppStore } from '../../store/states/app.state';

@Component({
  selector: 'wk-compare-versions',
  templateUrl: './wk-compare-versions.component.html',
  styleUrls: ['./wk-compare-versions.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class WkCompareVersionsComponent implements OnDestroy {
  @Output() versionsSelected = new EventEmitter<{
    current: DocumentVersion;
    target: DocumentVersion;
  }>();

  file: Node;
  loading = true;
  currentNodeRef: any;
  targetNodeRef: any;

  currentVersion: DocumentVersion = null;
  targetVersion: DocumentVersion = null;

  currentVersions: DocumentVersion[];
  targetVersions: DocumentVersion[];
  onDestroy = new Subject<any>();

  private versions: DocumentVersion[] = [];

  constructor(
    private readonly compareService: WkCompareService,
    private readonly store: Store<AppStore>,
    private readonly datePipe: DatePipe
  ) {}

  ngOnInit() {
    this.compareService
      .getCurrentVersion()
      .pipe(takeUntil(this.onDestroy))
      .subscribe((data) => {
        if (data) {
          this.currentNodeRef = data;
        }
      });
    this.compareService
      .getTargetVersion()
      .pipe(takeUntil(this.onDestroy))
      .subscribe((data) => {
        if (data) {
          this.targetNodeRef = data;
        }
      });
    this.store
      .select(getAppSelection)
      .pipe(
        filter(({ file }) => Boolean(file)),
        takeUntil(this.onDestroy)
      )
      .subscribe(({ file }) => {
        this.file = file.entry;
        this.getVersions();
      });
  }

  ngOnDestroy(): void {
    this.onDestroy.next(true);
    this.onDestroy.complete();
    this.compareService.setCurrentVersion('');
    this.compareService.setTargetVersion('');
  }

  onVersionChange(): void {
    this.currentVersions = this.targetVersion
      ? this.versions.filter((el) => el.number !== this.targetVersion.number)
      : this.versions;
    this.targetVersions = this.currentVersion
      ? this.versions.filter((el) => el.number !== this.currentVersion.number)
      : this.versions;
    this.emitEvent();
  }

  swap(): void {
    if (this.currentVersion && this.targetVersion) {
      [this.currentVersion, this.targetVersion] = [
        this.targetVersion,
        this.currentVersion,
      ];
      if (this.currentVersion) {
        this.currentNodeRef = this.currentVersion;
      }
      if (this.targetVersion) {
        this.targetNodeRef = this.targetVersion;
      }
      this.onVersionChange();
    }
  }

  private emitEvent(): void {
    if (this.currentVersion && this.targetVersion) {
      this.versionsSelected.emit({
        current: this.currentVersion,
        target: this.targetVersion,
      });
    } else {
      this.versionsSelected.emit(null);
    }
  }

  private setVersionsArrays(): void {
    this.currentVersions = this.targetVersion
      ? this.versions.filter((el) => el.number !== this.targetVersion.number)
      : this.versions;
    this.targetVersions = this.currentVersion
      ? this.versions.filter((el) => el.number !== this.currentVersion.number)
      : this.versions;
    if (this.currentNodeRef) {
      this.currentVersion = this.currentVersions.find(
        (el) => el.number === this.currentNodeRef.number
      );
      this.emitEvent();
    }
    if (this.targetNodeRef) {
      this.targetVersion = this.targetVersions.find(
        (el) => el.number === this.targetNodeRef.number
      );
      this.emitEvent();
    }
  }

  private getVersions(): void {
    this.compareService
      .getVersions(`workspace://SpacesStore/${this.file.id}`)
      .pipe(takeUntil(this.onDestroy))
      .subscribe((versions: DocumentVersion[]): void => {
        this.versions = this.processVersions(versions);
        if (!this.targetNodeRef) {
          this.setCurrentVersion();
        }
        this.setVersionsArrays();
        this.loading = false;
      });
  }

  private setCurrentVersion(): void {
    this.currentVersion = this.versions[0];
  }

  private processVersions(versions: DocumentVersion[]): DocumentVersion[] {
    return versions.map((el: DocumentVersion): DocumentVersion => {
      const checkedIn = this.datePipe.transform(
        new Date(el.checkedIn),
        'short'
      );
      return {
        ...el,
        checkedIn,
        title: `${el.number} ${el.name || this.file.name} ${checkedIn} ${
          el.createdBy
        }`,
      };
    });
  }
}
